import { SmartPreferenceBanner } from '@/src/components/common/srp/smartPreferenceBanner'
import TopSortCardsBadge from '@/src/components/common/srp/topSortCardsLabel/topSortCardsLabel'
import YellowBannerPromo from '@/src/components/common/srp/yellowBannerPromo'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import { useGetSortedTopCards } from '@/src/hooks/srp/getGetSortedTopCards'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { selectSRPFloatingAIChatEnabled } from '@/src/redux/slices/aiFeatureSlice'
import { useAppSelector } from '@/src/redux/store'
import { FlightCard as FlightCardType } from '@/src/types/flight'
import { TranslationFunction } from '@/src/types/translations'
import { getSmartPreferenceSectionState } from '@/src/utils/srp/aiRecommendation/preferenceSectionState/smartPreferenceSections'
import { extractFlightCardProps, GetFlightDataReturn } from '@/src/utils/srp/flightResultsUtil'
import { Box, Button, Card, Collapse, Typography } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import ReactList from 'react-list'
import { MobileFlightCard } from '../flightCard/mobileFlightCard'

interface FlightResultsMobileProps {
    groupedEntries: [string, FlightCardType[]][]
    smartPreferenceGroupedCards?: {
        preferred: [string, FlightCardType[]][]
        nonPreferred: [string, FlightCardType[]][]
    } | null
    getFlightData: (flight: FlightCardType) => GetFlightDataReturn
    route?: string
    onBookFlight?: (flight: FlightCardType) => void
    onViewDetails?: (flight: FlightCardType) => void
    onSortByPrice?: () => void
    sectors: any
    tCommon: TranslationFunction<'common'>
    tFlightDetails: TranslationFunction<'srpFlightDetails'>
    limitedView?: boolean
    limitedCount?: number
    showLoadMore?: boolean
    onLoadMore?: () => void
}

const FlightResultsMobile: React.FC<FlightResultsMobileProps> = ({
    groupedEntries = [],
    smartPreferenceGroupedCards = null,
    getFlightData,
    onBookFlight,
    sectors,
    onViewDetails,
    tFlightDetails,
    tCommon,
    limitedView = false,
    limitedCount = 5,
    showLoadMore = false,
    onLoadMore
}) => {
    const srpFloatingAIChatEnabled = useAppSelector(selectSRPFloatingAIChatEnabled)
    const [expandedPrice, setExpandedPrice] = useState<string | null>(null)
    const topSortedCards = useGetSortedTopCards()
    const { isPerPaxPricingEnabled, selectedSortForSelectedJourneyLeg } = useSearchResults()
    const isSmartSort = selectedSortForSelectedJourneyLeg?.sortKey === 'smart'

    // Handle limited view for flight schedule page
    const displayGroupedEntries = useMemo(() => {
        if (limitedView) {
            return groupedEntries.slice(0, limitedCount)
        }
        return groupedEntries
    }, [groupedEntries, limitedView, limitedCount])
    const {
        primaryEntries,
        secondaryEntries,
        shouldShowBannerBeforePrimary,
        shouldShowBannerBeforeSecondary,
        bannerVariant
    } =
        getSmartPreferenceSectionState({
            preferredEntries: smartPreferenceGroupedCards?.preferred,
            nonPreferredEntries: smartPreferenceGroupedCards?.nonPreferred,
            fallbackEntries: displayGroupedEntries,
            isSmartSort
        })
    const renderExpandedSamePriceCards = useCallback(
        (extraCards: FlightCardType[]) => {
            return (
                <Box sx={{ background: '#fafbfc', borderRadius: 2, p: 1, mt: 1, px: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {tFlightDetails('samePriceFlights')}
                        </Typography>
                        <Button
                            size="small"
                            onClick={() => setExpandedPrice(null)}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {tFlightDetails('hideMoreOption')}
                        </Button>
                    </Box>
                    <ReactList
                        length={extraCards.length}
                        itemRenderer={(idx, key) => {
                            const card = extraCards[idx]

                            const sortedTopCard = topSortedCards.find(
                                sortOption => sortOption?.card?.id === card.id
                            )

                            const cardNode = (
                                <Card
                                    sx={{
                                        border: '1px solid #eee',
                                        borderRadius: 2,
                                        background: '#fff',
                                        mb: 2
                                    }}
                                >
                                    <MobileFlightCard
                                        {...extractFlightCardProps(
                                            card,
                                            sectors,
                                            getFlightData,
                                            isPerPaxPricingEnabled
                                        )}
                                        onBook={() => onBookFlight?.(card)}
                                        onViewDetails={() => onViewDetails?.(card)}
                                        tFlightDetails={tFlightDetails}
                                        tCommon={tCommon}
                                    />
                                </Card>
                            )

                            if (sortedTopCard) {
                                return (
                                    <ErrorBoundaryWrapper
                                        identifier={`flight-results-mobile-card-${card.id}-PWA`}
                                        fallback={null}
                                        key={key}
                                    >
                                        <TopSortCardsBadge
                                            label={sortedTopCard.label}
                                            isActive={!!sortedTopCard.isActive}
                                            sortOptionId={sortedTopCard.id}
                                            isMobile={true}
                                            isCombined={sortedTopCard.isCombined}
                                        >
                                            {cardNode}
                                        </TopSortCardsBadge>
                                    </ErrorBoundaryWrapper>
                                )
                            }

                            return (
                                <ErrorBoundaryWrapper
                                    identifier={`flight-results-mobile-card-${card.id}-PWA`}
                                    fallback={null}
                                    key={key}
                                >
                                    {cardNode}
                                </ErrorBoundaryWrapper>
                            )
                        }}
                    />
                </Box>
            )
        },
        [getFlightData, onBookFlight, sectors, tCommon, tFlightDetails, onViewDetails, topSortedCards]
    )

    // const renderTopSortedCards = useCallback(() => {
    //     return (
    //         <Box p={1.5} borderRadius={1} bgcolor={"#F3F3F3"} mb={1} display={"flex"} flexDirection={"column"} gap={1}>
    //             {topSortedCards.map(sortOptions => (
    //                 <TopSortCardsBadge
    //                     key={sortOptions.id}
    //                     label={sortOptions.label}
    //                     isActive={!!sortOptions.isActive}
    //                     sortOptionId={sortOptions.id}
    //                     isMobile={true}
    //                     isCombined={sortOptions.isCombined}
    //                 >
    //                     <ErrorBoundaryWrapper
    //                         identifier={`flight-results-mobile-card-${sortOptions?.card?.id}-PWA`}
    //                         fallback={null}
    //                     >
    //                         <Box>
    //                             <Card
    //                                 sx={{
    //                                     border: '1px solid #eee',
    //                                     borderRadius: 2,
    //                                     background: '#fff',
    //                                     transition: 'border 0.2s, padding 0.2s',
    //                                     position: 'relative'
    //                                 }}
    //                             >
    //                                 <Box height={"10px"}></Box>
    //                                 <YellowBannerPromo promos={sortOptions?.card?.promos} isMobile />
    //                                 {!!sortOptions?.card?.promos?.length && <Box height={"10px"}></Box>}
    //                                 <Box
    //                                     sx={{
    //                                         pt: (sortOptions?.card?.promos?.length || 0) > 0 ? 1 : 0
    //                                     }}
    //                                 >
    //                                     <MobileFlightCard
    //                                         {...extractFlightCardProps(
    //                                             sortOptions?.card!,
    //                                             sectors,
    //                                             getFlightData,
    //                                             isPerPaxPricingEnabled
    //                                         )}
    //                                         onBook={() => onBookFlight?.(sortOptions?.card!)}
    //                                         onViewDetails={() => onViewDetails?.(sortOptions?.card!)}
    //                                         tFlightDetails={tFlightDetails}
    //                                         tCommon={tCommon}
    //                                     />
    //                                 </Box>
    //                             </Card>
    //                         </Box>
    //                     </ErrorBoundaryWrapper>
    //                 </TopSortCardsBadge>
    //             ))}
    //         </Box>
    //     )
    // }, [getFlightData, onBookFlight, onViewDetails, sectors, tCommon, tFlightDetails])

    const renderMainCard = useCallback(
        (entries: [string, FlightCardType[]][], index: number, key: React.Key) => {
            const [price, group] = entries[index] as [string, FlightCardType[]]
            const mainCard = group[0]
            const extraCards = group.slice(1)
            const isExpanded = expandedPrice === price

            const sortedTopCard = topSortedCards.find(sortOption => sortOption?.card?.id === mainCard.id)
            if (sortedTopCard) {
                return <Box mb={1} key={key} >
                    <TopSortCardsBadge
                        label={sortedTopCard.label}
                        isActive={!!sortedTopCard.isActive}
                        sortOptionId={sortedTopCard.id}
                        isMobile={true}
                        isCombined={sortedTopCard.isCombined}
                    >
                        <ErrorBoundaryWrapper
                            identifier={`flight-results-mobile-card-${sortedTopCard?.card?.id}-PWA`}
                            fallback={null}
                        >
                            <Box>
                                <Card
                                    sx={{
                                        border: '1px solid #eee',
                                        borderRadius: 2,
                                        background: '#fff',
                                        transition: 'border 0.2s, padding 0.2s',
                                        position: 'relative'
                                    }}
                                >
                                    <Box height={"10px"}></Box>
                                    <YellowBannerPromo promos={sortedTopCard?.card?.promos} isMobile />
                                    {!!sortedTopCard?.card?.promos?.length && <Box height={"10px"}></Box>}
                                    <Box
                                        sx={{
                                            pt: (sortedTopCard?.card?.promos?.length || 0) > 0 ? 1 : 0
                                        }}
                                    >
                                        <MobileFlightCard
                                            {...extractFlightCardProps(
                                                sortedTopCard?.card!,
                                                sectors,
                                                getFlightData,
                                                isPerPaxPricingEnabled
                                            )}
                                            onBook={() => onBookFlight?.(sortedTopCard?.card!)}
                                            onViewDetails={() => onViewDetails?.(sortedTopCard?.card!)}
                                            tFlightDetails={tFlightDetails}
                                            tCommon={tCommon}
                                        />
                                    </Box>
                                </Card>
                            </Box>
                        </ErrorBoundaryWrapper>
                    </TopSortCardsBadge>
                </Box>
            }

            return (
                <ErrorBoundaryWrapper
                    identifier={`flight-results-mobile-card-${mainCard.id}-PWA`}
                    fallback={null}
                    key={key}
                >
                    <Box sx={{ mb: 1 }}>
                        {/* Main Card */}
                        <Card
                            sx={{
                                border: '1px solid #eee',
                                borderRadius: 2,
                                background: '#fff',
                                cursor: extraCards.length > 0 ? 'pointer' : 'default',
                                transition: 'border 0.2s, padding 0.2s',
                                position: 'relative'
                            }}
                        >
                            <YellowBannerPromo promos={mainCard.promos} isMobile />
                            <Box sx={{ pt: mainCard.promos?.length > 0 ? 1 : 0 }}>
                                <MobileFlightCard
                                    {...extractFlightCardProps(
                                        mainCard,
                                        sectors,
                                        getFlightData,
                                        isPerPaxPricingEnabled
                                    )}
                                    onBook={() => onBookFlight?.(mainCard)}
                                    onViewDetails={() => onViewDetails?.(mainCard)}
                                    tFlightDetails={tFlightDetails}
                                    tCommon={tCommon}
                                />
                            </Box>
                        </Card>

                        {/* Expanded section: label, hide button, and extra cards */}
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            {renderExpandedSamePriceCards(extraCards)}
                        </Collapse>

                        {/* Show the 'N more at same price' bar if not expanded */}
                        {!isExpanded && extraCards.length > 0 && (
                            <Box
                                onClick={() => setExpandedPrice(price)}
                                sx={{
                                    cursor: 'pointer',
                                    width: '90%',
                                    height: '23px',
                                    mx: 'auto',
                                    borderBottomLeftRadius: '12px',
                                    borderBottomRightRadius: '12px',
                                    background: '#F3F3F3',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    color: 'primary.main'
                                }}
                            >
                                +{extraCards.length} {tFlightDetails('moreOptionAvailiable')}
                            </Box>
                        )}
                    </Box>
                </ErrorBoundaryWrapper>
            )
        },
        [
            expandedPrice,
            onBookFlight,
            onViewDetails,
            sectors,
            getFlightData,
            tFlightDetails,
            tCommon,
            topSortedCards
        ]
    )

    return (
        <>
            {/* {renderTopSortedCards()} */}
            <Box
                px={1}
                sx={
                    srpFloatingAIChatEnabled
                        ? {
                            // Clearance for fixed SRPFloatingAIChat (header + optional thread + composer + footer).
                            pb: 'calc(300px + env(safe-area-inset-bottom, 0px))'
                        }
                        : undefined
                }
            >
                {shouldShowBannerBeforePrimary && (
                    <SmartPreferenceBanner tCommon={tCommon} variant={bannerVariant} compact />
                )}
                <ReactList
                    length={primaryEntries.length}
                    itemRenderer={(index, key) => renderMainCard(primaryEntries, index, key)}
                    threshold={200}
                />
                {shouldShowBannerBeforeSecondary && (
                    <SmartPreferenceBanner tCommon={tCommon} variant={bannerVariant} compact />
                )}
                <ReactList
                    length={secondaryEntries.length}
                    itemRenderer={(index, key) => renderMainCard(secondaryEntries, index, key)}
                    threshold={200}
                />
                {limitedView && showLoadMore && groupedEntries.length > limitedCount && (
                    <Box
                        p={2}
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        // borderRadius={1.5}
                        sx={{
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)', // Safari support
                            backgroundColor: 'rgba(255, 255, 255, 0.635)',
                            // border: '1px solid #eee',
                            borderTop: '2px solid rgba(255, 255, 255, 0.2)'
                        }}
                    >
                        <Button variant="customVariantPrimary" onClick={onLoadMore}>
                            {tCommon('loadMore')}
                        </Button>
                    </Box>
                )}
            </Box>
        </>
    )
}

export { FlightResultsMobile }
